/**
 * Section clipboard — copy/paste of whole sections via localStorage, shared
 * across tabs. Extracted verbatim from shell.js (optimization Phase 10a);
 * selection-dependent wrappers (copySelectedSection, pasteSectionAfterSelection)
 * remain in shell.js, which owns selection state.
 */

import { store } from '../core/state.js';
import { eventBus } from '../core/events.js';
import * as sectionManager from '../sections/section-manager.js';
import { showToast } from './toast.js';
import { showUndoToast } from './undo-toast.js';

const CLIPBOARD_KEY = 'pb:section-clipboard';
const CLIPBOARD_VERSION = 1;

// PP-2: no shell import. The shell injects the ONE thing this module needs
// from it — the preview renderer — at boot; toasts come from the toast module
// directly. That is what lets mutation-undo-tests drive a paste with no shell
// in the process, and what removed the clipboard ↔ shell import cycle.
let _setSelectedInstanceId = () => {};
let _renderPreview = async () => {};
export function _initSectionClipboard({ setSelectedInstanceId, renderPreview } = {}) {
  if (typeof setSelectedInstanceId === 'function') _setSelectedInstanceId = setSelectedInstanceId;
  if (typeof renderPreview === 'function') _renderPreview = renderPreview;
}

function generateInstanceId() {
  return 'inst-' + crypto.randomUUID().slice(0, 8);
}

function _removeSectionClipboardSilently() {
  try {
    localStorage.removeItem(CLIPBOARD_KEY);
  } catch {
    // Ignore storage errors while cleaning invalid clipboard payloads.
  }
}

export function getSectionClipboard() {
  let raw = null;
  try {
    raw = localStorage.getItem(CLIPBOARD_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let payload = null;
  try {
    payload = JSON.parse(raw);
  } catch {
    _removeSectionClipboardSilently();
    return null;
  }

  if (
    !payload ||
    payload.version !== CLIPBOARD_VERSION ||
    !payload.section ||
    typeof payload.section !== 'object'
  ) {
    _removeSectionClipboardSilently();
    return null;
  }

  return payload.section;
}

export function hasSectionClipboard() {
  return getSectionClipboard() != null;
}

/**
 * PP-1: where the copied section came from. The payload has always recorded
 * `projectName` and `copiedAt`; the picker's clipboard tile is the first
 * surface to show them. Same parse, same validity rules as getSectionClipboard.
 * @returns {{projectName: string|null, copiedAt: number|null}|null}
 */
export function getSectionClipboardMeta() {
  if (!getSectionClipboard()) return null;
  try {
    const payload = JSON.parse(localStorage.getItem(CLIPBOARD_KEY));
    return {
      projectName: typeof payload?.projectName === 'string' ? payload.projectName : null,
      copiedAt: Number.isFinite(payload?.copiedAt) ? payload.copiedAt : null
    };
  } catch {
    return { projectName: null, copiedAt: null };
  }
}

function setSectionClipboard(section) {
  const payload = {
    version: CLIPBOARD_VERSION,
    section,
    projectName: store.getState()?.projectName ?? null,
    copiedAt: Date.now()
  };
  try {
    localStorage.setItem(CLIPBOARD_KEY, JSON.stringify(payload));
  } catch {
    return false;
  }
  eventBus.emit('clipboard:changed');
  return true;
}

export function clearSectionClipboard() {
  try {
    localStorage.removeItem(CLIPBOARD_KEY);
  } catch {
    // ignore — nothing to clear from our perspective
  }
  eventBus.emit('clipboard:changed');
}

export function copySectionById(instanceId) {
  if (!instanceId) return false;

  const sharedType = sectionManager.getSharedType(instanceId);
  if (sharedType) {
    showToast('Shared sections cannot be copied yet', 'info');
    return false;
  }

  const state = store.getState();
  if (!state) return false;

  for (const page of state.pages) {
    const section = page.sections.find((s) => s.instanceId === instanceId);
    if (section) {
      const ok = setSectionClipboard(structuredClone(section));
      if (ok) showToast('Section copied');
      else showToast('Clipboard unavailable. Check browser storage settings.', 'error');
      return ok;
    }
  }
  return false;
}

export async function pasteSectionAt(pageIndex, insertIndex) {
  const section = getSectionClipboard();
  if (!section) {
    showToast('Copy a section first', 'info');
    return false;
  }

  const state = store.getState();
  if (!state || !state.pages?.[pageIndex]) return false;

  const page = state.pages[pageIndex];
  const clampedIndex = Math.max(0, Math.min(insertIndex, page.sections.length));

  const clone = structuredClone(section);
  clone.instanceId = generateInstanceId();
  sectionManager.matchCurrentButtonMarkup(clone);
  const inserted = store.insertAt(
    `pages.${pageIndex}.sections`,
    clampedIndex,
    clone,
    'Paste section'
  );
  if (!inserted) return false;
  const pasteEntry = store.history.entries[store.historyPosition] || null;
  try {
    await _renderPreview({ preserveScroll: true, transition: null, hideDuringLoad: false });
  } catch (err) {
    // Rendering is part of the user-visible paste contract. If it fails while
    // our insertion is still the latest mutation, roll back exactly that
    // entry before allowing the picker engine to report the rejection. The
    // identity guard prevents an async failure from undoing newer user work.
    if (pasteEntry && store.history.entries[store.historyPosition] === pasteEntry) {
      store.undo();
    }
    throw err;
  }

  _setSelectedInstanceId(clone.instanceId);
  // PP-2: "it landed here" — the shell answers this by selecting, scrolling to
  // and flashing the new section (selectAfterRender), the same contract an
  // add from the picker gets. Emitted AFTER the render so the element exists.
  eventBus.emit('section:landed', { instanceId: clone.instanceId });
  // And a way back: the undo IS the confirmation. Created after the render so
  // the history-changed sweep has already run.
  showUndoToast('Section pasted', { id: 'undo-paste-section' });
  return true;
}
